/**
 * Report Writer — writes report data structures to formatted Excel sheets.
 *
 * All functions receive an Excel context (from an outer Excel.run) so they can
 * share a single batch context with the orchestrator.  They MUST call
 * `context.sync()` internally when they need values from Excel before
 * proceeding, but they do NOT open their own `Excel.run`.
 */

import { CONFIG } from '../config/constants.js';

const MONTH_NAMES = CONFIG.DATE.MONTH_NAMES;

// ── Colour palette ───────────────────────────────────────────────────────────

const NAVY        = '#1F4E79';
const WHITE       = '#FFFFFF';
const SECTION_BG  = '#EBF3FB';
const SECTION_FG  = '#1F3864';
const CALC_BG     = '#F2F2F2';
const CALC_BOLD_BG = '#E2EFDA';
const ACCENT      = '#2E75B6';
const GREY_TEXT   = '#666666';
const AMBER_BG    = '#FFF3E0';

const CHUNK = 500;

// ── Number format by numbersIn setting ───────────────────────────────────────

function getNumberFormat(numbersIn) {
  if (numbersIn === 1 || numbersIn === 'Thousands') return CONFIG.EXCEL.NUMBER_FORMAT_THOUSANDS;
  if (numbersIn === 2 || numbersIn === 'Millions') return CONFIG.EXCEL.NUMBER_FORMAT_MILLIONS;
  return CONFIG.EXCEL.NUMBER_FORMAT;
}

function colLetter(idx) {
  let s = '';
  let n = idx;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

// ── Sheet helpers ────────────────────────────────────────────────────────────

async function getOrCreateSheet(context, sheetName) {
  let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
  sheet.load('isNullObject');
  await context.sync();
  if (sheet.isNullObject) {
    sheet = context.workbook.worksheets.add(sheetName);
    await context.sync();
  } else {
    // Remove any freeze panes left from a previous run
    try { sheet.freezePanes.unfreeze(); await context.sync(); } catch { /* ignore */ }

    // Delete any existing tables on this sheet before clearing
    const tables = sheet.tables;
    tables.load('items');
    await context.sync();
    for (let i = tables.items.length - 1; i >= 0; i--) {
      tables.items[i].delete();
    }
    if (tables.items.length > 0) await context.sync();

    const used = sheet.getUsedRangeOrNullObject();
    used.load('isNullObject');
    await context.sync();
    if (!used.isNullObject) {
      used.clear(Excel.ClearApplyTo.all);
      await context.sync();
    }
  }
  return sheet;
}

// ── Trial Balance Writer ─────────────────────────────────────────────────────

/**
 * Writes the flat trial balance report as an Excel table.
 *
 * @param {Excel.RequestContext} context
 * @param {string} sheetName
 * @param {{ title, headers, rows, displayMonths }} report
 * @param {object} cfg
 */
export async function writeTrialBalanceReport(context, sheetName, report, cfg) {
  const sheet = await getOrCreateSheet(context, sheetName);
  const numFmt = getNumberFormat(cfg.numbersIn);

  // Row 1 — title
  const lastCol = colLetter(report.headers.length - 1);
  const titleRange = sheet.getRange(`A1:${lastCol}1`);
  titleRange.merge(false);
  sheet.getRange('A1').values = [[`${report.title}  —  ${cfg.year || 'All Years'}`]];
  titleRange.format.fill.color = NAVY;
  titleRange.format.font.color = WHITE;
  titleRange.format.font.bold = true;
  titleRange.format.font.size = 14;
  titleRange.format.rowHeight = 36;
  titleRange.format.verticalAlignment = 'Center';
  await context.sync();

  // Row 2 — filter summary
  const summary = `Year=${cfg.year || 'All'}  |  Companies=${cfg.partyIds?.length > 0 ? cfg.partyIds.length + ' selected' : 'All'}  |  Period=${cfg.periodType === 12 ? 'Full year' : (cfg.period || 'All')}`;
  sheet.getRange('A2').values = [[summary]];
  sheet.getRange('A2').format.font.size = 9;
  sheet.getRange('A2').format.font.color = GREY_TEXT;
  sheet.getRange('A2').format.font.italic = true;
  sheet.getRange('A2').format.rowHeight = 22;

  // Row 3 — headers
  const hdrRange = sheet.getRange(`A3:${lastCol}3`);
  hdrRange.values = [report.headers];
  hdrRange.format.font.bold = true;
  hdrRange.format.fill.color = NAVY;
  hdrRange.format.font.color = WHITE;
  hdrRange.format.font.size = 10;
  hdrRange.format.rowHeight = 26;
  hdrRange.format.verticalAlignment = 'Center';
  await context.sync();

  if (report.rows.length === 0) {
    sheet.getRange('A4').values = [['No data matches the current filter settings.']];
    sheet.getRange('A4').format.font.italic = true;
    await context.sync();
    return;
  }

  // Write data in chunks
  for (let i = 0; i < report.rows.length; i += CHUNK) {
    const chunk = report.rows.slice(i, i + CHUNK);
    const startRow = 4 + i;
    const endRow = startRow + chunk.length - 1;
    sheet.getRange(`A${startRow}:${lastCol}${endRow}`).values = chunk;
    await context.sync();
  }

  const labelCols = report.labelColumnCount ?? 6;
  const amountStart = colLetter(labelCols);
  const dataEnd = 3 + report.rows.length;
  const amountCount = report.headers.length - labelCols;
  if (amountCount > 0 && report.rows.length > 0) {
    sheet.getRange(`${amountStart}4:${lastCol}${dataEnd}`).numberFormat =
      Array(report.rows.length).fill(Array(amountCount).fill(numFmt));
  }

  // Create Excel table
  const tableRange = sheet.getRange(`A3:${lastCol}${dataEnd}`);
  const existTbl = sheet.tables.getItemOrNullObject('tbl_RptTrialBalance');
  existTbl.load('isNullObject');
  await context.sync();
  if (!existTbl.isNullObject) { existTbl.delete(); await context.sync(); }

  const tbl = sheet.tables.add(tableRange, true);
  tbl.name = 'tbl_RptTrialBalance';
  tbl.style = 'TableStyleMedium2';
  await context.sync();

  sheet.getRange('A:A').format.columnWidth = 72;
  sheet.getRange('B:B').format.columnWidth = 180;
  sheet.getRange('C:C').format.columnWidth = 100;
  sheet.getRange('D:D').format.columnWidth = 200;
  sheet.getRange('E:E').format.columnWidth = 100;
  sheet.getRange('F:F').format.columnWidth = 200;
  try { sheet.getUsedRange().format.autofitColumns(); await context.sync(); } catch { /* best-effort */ }
}

/**
 * Minimal sheet content when a report is not built (e.g. Trial balance only mode).
 */
export async function writePlaceholderReport(context, sheetName, message) {
  const sheet = await getOrCreateSheet(context, sheetName);
  sheet.getRange('A1').values = [[String(message || 'Report not generated.')]];
  sheet.getRange('A1').format.font.italic = true;
  sheet.getRange('A1').format.font.size = 12;
  sheet.getRange('A1').format.wrapText = true;
  await context.sync();
}

// ── Hierarchical Report Writer (P&L / BS / CF) ──────────────────────────────

/**
 * Writes a hierarchical report (sections + child accounts) to an Excel sheet.
 *
 * @param {Excel.RequestContext} context
 * @param {string} sheetName
 * @param {{ title, sections, displayMonths }} report
 * @param {object} cfg
 */
export async function writeHierarchicalReport(context, sheetName, report, cfg) {
  const sheet = await getOrCreateSheet(context, sheetName);
  const numFmt = getNumberFormat(cfg.numbersIn);
  const dm = report.displayMonths;
  const showYtd = report.showYtd !== false; // default true; BS sets false

  // Column layout: A = Account Name, B..N = months, optionally last = YTD
  const totalCols = 1 + dm.length + (showYtd ? 1 : 0);
  const lastCol = colLetter(totalCols - 1);
  const numAmountCols = dm.length + (showYtd ? 1 : 0);

  // ── Row 1 — Title ──────────────────────────────────────────────────────
  const titleRange = sheet.getRange(`A1:${lastCol}1`);
  titleRange.merge(false);
  sheet.getRange('A1').values = [[`${report.title}  —  ${cfg.year || ''}`]];
  titleRange.format.fill.color = NAVY;
  titleRange.format.font.color = WHITE;
  titleRange.format.font.bold = true;
  titleRange.format.font.size = 14;
  titleRange.format.rowHeight = 36;
  titleRange.format.verticalAlignment = 'Center';

  // ── Row 2 — Filter summary ────────────────────────────────────────────
  const summary = `Year=${cfg.year || 'All'}  |  Companies=${cfg.partyIds?.length > 0 ? cfg.partyIds.length + ' selected' : 'All'}  |  Period=${cfg.periodType === 12 ? 'Full year' : (cfg.period || 'All')}`;
  sheet.getRange('A2').values = [[summary]];
  sheet.getRange('A2').format.font.size = 9;
  sheet.getRange('A2').format.font.color = GREY_TEXT;
  sheet.getRange('A2').format.font.italic = true;
  sheet.getRange('A2').format.rowHeight = 20;

  // ── Row 3 — Column headers ────────────────────────────────────────────
  const headers = ['Account Name'];
  for (const m of dm) headers.push(MONTH_NAMES[m - 1]);
  if (showYtd) headers.push('YTD');

  const hdrRange = sheet.getRange(`A3:${lastCol}3`);
  hdrRange.values = [headers];
  hdrRange.format.font.bold = true;
  hdrRange.format.fill.color = NAVY;
  hdrRange.format.font.color = WHITE;
  hdrRange.format.font.size = 10;
  hdrRange.format.rowHeight = 26;
  hdrRange.format.verticalAlignment = 'Center';
  hdrRange.format.horizontalAlignment = 'Center';
  sheet.getRange('A3').format.horizontalAlignment = 'Left';
  await context.sync();

  // ── Build all rows in memory first, then write in one batch ────────────
  const dataRows = [];
  const rowStyles = [];

  for (const section of report.sections) {
    if (section.isCalculated) {
      const row = [section.name];
      for (const v of section.monthlyTotals) row.push(v);
      if (showYtd) row.push(section.ytdTotal);
      dataRows.push(row);
      rowStyles.push(section.isBold ? 'calcBold' : 'calc');
    } else {
      // Section header row
      const row = [section.name];
      for (const v of section.monthlyTotals) row.push(v);
      if (showYtd) row.push(section.ytdTotal);
      dataRows.push(row);
      rowStyles.push(section.property === 'Unclassified' ? 'unclassified' : 'section');

      // Child account rows — format: "AccountName (AccountNumber)" matching Blazor
      for (const child of section.children) {
        const childRow = [`   ${child.name} (${child.accountNumber})`];
        for (const v of child.monthlyAmounts) childRow.push(v);
        if (showYtd) childRow.push(child.ytdAmount);
        dataRows.push(childRow);
        rowStyles.push('child');
      }
    }
  }

  if (dataRows.length === 0) {
    sheet.getRange('A4').values = [['No data matches the current filter settings.']];
    sheet.getRange('A4').format.font.italic = true;
    await context.sync();
    return;
  }

  // Write all data starting at row 4
  const startRow = 4;
  const lastDataRow = startRow + dataRows.length - 1;
  for (let i = 0; i < dataRows.length; i += CHUNK) {
    const chunk = dataRows.slice(i, i + CHUNK);
    const r1 = startRow + i;
    const r2 = r1 + chunk.length - 1;
    sheet.getRange(`A${r1}:${lastCol}${r2}`).values = chunk;
    await context.sync();
  }

  // ── Number format for amount columns ───────────────────────────────────
  const amountStartCol = colLetter(1);
  sheet.getRange(`${amountStartCol}${startRow}:${lastCol}${lastDataRow}`).numberFormat =
    Array(dataRows.length).fill(Array(numAmountCols).fill(numFmt));
  await context.sync();

  // ── Apply formatting per row ───────────────────────────────────────────
  try {
    for (let i = 0; i < rowStyles.length; i++) {
      const excelRow = startRow + i;
      const rowRange = sheet.getRange(`A${excelRow}:${lastCol}${excelRow}`);

      const style = rowStyles[i];
      switch (style) {
        case 'section':
          rowRange.format.fill.color = SECTION_BG;
          rowRange.format.font.color = SECTION_FG;
          rowRange.format.font.bold = true;
          rowRange.format.font.size = 10;
          rowRange.format.rowHeight = 26;
          break;

        case 'unclassified':
          rowRange.format.fill.color = AMBER_BG;
          rowRange.format.font.color = SECTION_FG;
          rowRange.format.font.bold = true;
          rowRange.format.font.size = 10;
          rowRange.format.rowHeight = 26;
          break;

        case 'calc':
          rowRange.format.fill.color = CALC_BG;
          rowRange.format.font.bold = true;
          rowRange.format.font.size = 10;
          rowRange.format.rowHeight = 24;
          rowRange.format.borders.getItem('EdgeTop').style = 'Continuous';
          rowRange.format.borders.getItem('EdgeTop').color = '#BFBFBF';
          break;

        case 'calcBold':
          rowRange.format.fill.color = CALC_BOLD_BG;
          rowRange.format.font.bold = true;
          rowRange.format.font.size = 11;
          rowRange.format.rowHeight = 28;
          rowRange.format.borders.getItem('EdgeTop').style = 'Continuous';
          rowRange.format.borders.getItem('EdgeTop').color = ACCENT;
          rowRange.format.borders.getItem('EdgeTop').weight = 'Medium';
          rowRange.format.borders.getItem('EdgeBottom').style = 'Continuous';
          rowRange.format.borders.getItem('EdgeBottom').color = ACCENT;
          break;

        case 'child':
          rowRange.format.font.size = 10;
          rowRange.format.rowHeight = 22;
          rowRange.format.font.color = '#333333';
          break;
      }

      if ((i + 1) % 50 === 0) await context.sync();
    }
    await context.sync();
  } catch (fmtErr) {
    console.warn('[writeHierarchicalReport] Row formatting error (non-fatal):', fmtErr.message);
    try { await context.sync(); } catch { /* ignore */ }
  }

  // ── Column widths — exact same pattern as trial balance (which works) ──
  sheet.getRange('A:A').format.columnWidth = 300;
  sheet.getRange('B:B').format.columnWidth = 130;
  try { sheet.getUsedRange().format.autofitColumns(); await context.sync(); } catch { /* best-effort */ }
}
